/**
 * 공지사항 API (서버 GET /notices, /notices/{slug}) — 공지는 DB에 저장되고 홈페이지는 조회만 한다.
 * 서버 CORS(CorsConfig)가 www.cupidlab.io / cupidlab.io 출처만 허용한다. 로컬 개발 시 VITE_API_BASE_URL로 바꾼다.
 */
export type NoticeCategory = 'POLICY' | 'SERVICE' | 'MAINTENANCE';

export interface NoticeSummary {
  slug: string;
  category: NoticeCategory;
  title: string;
  /** ISO-8601 UTC */
  publishedAt: string;
}

export interface Notice extends NoticeSummary {
  /** Markdown */
  body: string;
}

export interface NoticePage {
  items: NoticeSummary[];
  page: number;
  size: number;
  hasNext: boolean;
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'https://was.cupidlab.io';

export class NoticeNotFoundError extends Error {}

const NOT_FOUND_CODE = 'F40402';

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { headers: { Accept: 'application/json' } });
  if (res.status === 404) throw new NoticeNotFoundError();
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = (await res.json()) as { result: string; code: string; data: T };
  // 서버 규약: 오류는 실제 HTTP 상태(없는 공지 404) + result:'fail' + 코드. 예전 서버(HTTP 200 + fail)도 받는다. F40402 = ENTITY_NOT_FOUND
  if (json.result !== 'success') {
    if (json.code === NOT_FOUND_CODE) throw new NoticeNotFoundError();
    throw new Error(json.code);
  }
  return json.data;
}

export function fetchNotices(page = 0, size = 20): Promise<NoticePage> {
  return get<NoticePage>(`/notices?page=${page}&size=${size}`);
}

export function fetchNotice(slug: string): Promise<Notice> {
  return get<Notice>(`/notices/${encodeURIComponent(slug)}`);
}

export const CATEGORY_LABEL: Record<NoticeCategory, string> = {
  POLICY: '정책',
  SERVICE: '서비스',
  MAINTENANCE: '점검',
};

/** 게시일 표시 — 기기(브라우저) 로컬 날짜, YYYY.MM.DD */
export function formatNoticeDate(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
}
